import { Box, CategoryAreaPinComponent, PinComponent, Vector } from '@lessonup/pins-shared';
import { CategorySettings } from '../../pinComponents/CategoryAreaPinComponent/Category';
import { CategoryTransformAction } from '../../types/pinComponents/pinTransformActions';
import { calculateRotationAngle, moveBox, resizeBox, rotateBox } from '../geometry';
import { DEFAULT_MIN_SIZE } from './pinComponentActions';

interface ContainerSize {
  width: number;
  height: number;
}

export const pixelsToPercentage = (pixels: number, containerSize: number): number => {
  if (containerSize === 0) return 0; // Prevent division by zero
  return (pixels / containerSize) * 100;
};

export const percentageToPixels = (percentage: number, containerSize: number): number => {
  return (percentage / 100) * containerSize;
};

export const isUsingPercentages = (category: CategorySettings): boolean => {
  if (category.layout.type !== 'BOX') return false;
  return (
    category.layout.position.x <= 100 &&
    category.layout.position.y <= 100 &&
    category.layout.size.width <= 100 &&
    category.layout.size.height <= 100
  );
};

export const convertToPixelPosition = (category: CategorySettings, containerSize: ContainerSize): CategorySettings => {
  if (!isUsingPercentages(category)) {
    return category;
  }

  return {
    ...category,
    layout: {
      ...category.layout,
      position: {
        x: percentageToPixels(category.layout.position.x, containerSize.width),
        y: percentageToPixels(category.layout.position.y, containerSize.height),
      },
      size: {
        width: percentageToPixels(category.layout.size.width, containerSize.width),
        height: percentageToPixels(category.layout.size.height, containerSize.height),
      },
    },
  };
};

export const convertToPercentagePosition = (
  category: CategorySettings,
  containerSize: ContainerSize
): CategorySettings => {
  if (category.layout.type !== 'BOX' || isUsingPercentages(category)) {
    return category;
  }

  return {
    ...category,
    layout: {
      ...category.layout,
      position: {
        x: pixelsToPercentage(category.layout.position.x, containerSize.width),
        y: pixelsToPercentage(category.layout.position.y, containerSize.height),
      },
      size: {
        width: pixelsToPercentage(category.layout.size.width, containerSize.width),
        height: pixelsToPercentage(category.layout.size.height, containerSize.height),
      },
    },
  };
};

const constrainBoxToContainer = (box: Box, containerSize: ContainerSize): Box => {
  const { position, size } = box;

  const constrainedPosition = {
    x: Math.max(0, Math.min(position.x, containerSize.width - size.width)),
    y: Math.max(0, Math.min(position.y, containerSize.height - size.height)),
  };

  const constrainedSize = {
    width: Math.min(size.width, containerSize.width - constrainedPosition.x),
    height: Math.min(size.height, containerSize.height - constrainedPosition.y),
  };

  return {
    ...box,
    position: constrainedPosition,
    size: constrainedSize,
  };
};

const convertBoxToPercentageCategory = (box: Box, containerSize: ContainerSize): CategorySettings['layout'] => {
  return {
    type: 'BOX',
    position: {
      x: pixelsToPercentage(box.position.x, containerSize.width),
      y: pixelsToPercentage(box.position.y, containerSize.height),
    },
    size: {
      width: pixelsToPercentage(box.size.width, containerSize.width),
      height: pixelsToPercentage(box.size.height, containerSize.height),
    },
    rotation: box.rotation,
  };
};

const getContainerSize = (categoryAreaComponent: PinComponent): ContainerSize => {
  if (categoryAreaComponent.layout.type === 'BOX') {
    return {
      width: categoryAreaComponent.layout.size.width,
      height: categoryAreaComponent.layout.size.height,
    };
  }
  // Fallback
  return { width: 400, height: 300 };
};

/**
 * Transform a CategoryArea component by applying transforms to one of its categories
 * This integrates with the existing editor transform system while preserving category-specific logic
 */
export function transformCategoryAreaComponent(
  categoryAreaComponent: CategoryAreaPinComponent,
  deltaMouse: Vector,
  mousePosition: Vector,
  action: CategoryTransformAction
): PinComponent {
  const settings = categoryAreaComponent.settings;
  const { categories } = settings;
  const category = categories.find((cat: CategorySettings) => cat.id === action.categoryId);

  if (!category) {
    return categoryAreaComponent;
  }

  const containerSize = getContainerSize(categoryAreaComponent);

  const pixelCategory = convertToPixelPosition(category, containerSize);
  const categoryBox: Box = {
    type: 'BOX',
    position: pixelCategory.layout.position,
    size: pixelCategory.layout.size,
    rotation: pixelCategory.layout.rotation,
  };

  let transformedBox: Box;
  switch (action.action) {
    case 'move-category':
      transformedBox = moveBox(categoryBox, deltaMouse);
      break;
    case 'resize-category':
      transformedBox = resizeBox(categoryBox, deltaMouse, action.grippy, false, DEFAULT_MIN_SIZE);
      break;
    case 'rotate-category':
      transformedBox = rotateBox(categoryBox, calculateRotationAngle(categoryBox, mousePosition));
      break;
    default:
      transformedBox = categoryBox;
  }

  const constrainedBox = constrainBoxToContainer(transformedBox, containerSize);

  const updatedCategoryLayout = convertBoxToPercentageCategory(constrainedBox, containerSize);
  const updatedCategory: CategorySettings = {
    ...category,
    layout: updatedCategoryLayout,
  };

  return {
    ...categoryAreaComponent,
    settings: {
      ...settings,
      categories: categories.map((cat: CategorySettings) => (cat.id === action.categoryId ? updatedCategory : cat)),
    },
  };
}
